/**
 * Everyone CDR has a record of, filterable by vendor organization, sign-in status, and grant count.
 */

import { useCollection } from "@cloudscape-design/collection-hooks"
import Box from "@cloudscape-design/components/box"
import Container from "@cloudscape-design/components/container"
import ContentLayout from "@cloudscape-design/components/content-layout"
import Header from "@cloudscape-design/components/header"
import Link from "@cloudscape-design/components/link"
import Pagination from "@cloudscape-design/components/pagination"
import PropertyFilter from "@cloudscape-design/components/property-filter"
import StatusIndicator from "@cloudscape-design/components/status-indicator"
import Table from "@cloudscape-design/components/table"
import { useMemo } from "react"
import { useNavigate } from "react-router-dom"

import { usePrincipalsList } from "@/hooks/queries/usePrincipals"
import { useVendorList } from "@/hooks/queries/useVendors"
import { parseApiErrorMessage } from "@/utils/apiErrors"

const PEOPLE_PER_PAGE = 20

const matchText = (n: number) => `${n} ${n === 1 ? "match" : "matches"}`

const FILTERING_PROPERTIES = [
  { key: "email", propertyLabel: "Email", groupValuesLabel: "Emails", operators: ["=", "!=", ":"] },
  {
    key: "organization",
    propertyLabel: "Organization",
    groupValuesLabel: "Organizations",
    operators: ["=", "!="],
  },
  { key: "status", propertyLabel: "Status", groupValuesLabel: "Statuses", operators: ["=", "!="] },
] as const

/**
 * Admin directory of principals.
 */
export default function PrincipalDirectory() {
  const navigate = useNavigate()
  const { principals, isPending, error } = usePrincipalsList()
  const { data: vendors = [] } = useVendorList()
  // Carry the Organization and Status cell text so filters match it.
  const rows = useMemo(() => {
    const vendorNames = new Map(vendors.map((v) => [v.vendor_id, v.vendor_name]))
    return principals.map((p) => ({
      ...p,
      organization: p.company_id ? (vendorNames.get(p.company_id) ?? p.company_id) : "-",
      status: p.status === "active" ? "Active" : "Pending",
    }))
  }, [principals, vendors])
  const { items, collectionProps, propertyFilterProps, paginationProps, filteredItemsCount } =
    useCollection(rows, {
      propertyFiltering: { filteringProperties: [...FILTERING_PROPERTIES] },
      pagination: { pageSize: PEOPLE_PER_PAGE },
    })
  const filteringOptions = FILTERING_PROPERTIES.flatMap(({ key }) =>
    [...new Set(rows.map((r) => r[key]))].map((value) => ({
      propertyKey: key,
      value,
    })),
  )

  return (
    <ContentLayout
      header={
        <Header variant="h1" description="Everyone CDR has a record of, and the access they hold">
          People
        </Header>
      }
    >
      <Container>
        <Table
          {...collectionProps}
          variant="embedded"
          items={items}
          loading={isPending}
          loadingText="Loading people"
          trackBy="principal_id"
          empty={
            <Box color="text-body-secondary">
              {error
                ? parseApiErrorMessage(error, "Failed to load people")
                : rows.length > 0
                  ? "No people match the filter."
                  : "No people yet."}
            </Box>
          }
          filter={
            <PropertyFilter
              {...propertyFilterProps}
              filteringOptions={filteringOptions}
              filteringPlaceholder="Filter people by email, organization or status"
              filteringAriaLabel="Filter people"
              countText={matchText(filteredItemsCount ?? rows.length)}
              expandToViewport
            />
          }
          pagination={<Pagination {...paginationProps} />}
          columnDefinitions={[
            {
              id: "email",
              header: "Email",
              cell: (p) => (
                <Link
                  href={`/principals/${p.principal_id}`}
                  onFollow={(e) => {
                    e.preventDefault()
                    navigate(`/principals/${p.principal_id}`)
                  }}
                >
                  {p.email}
                </Link>
              ),
            },
            {
              id: "organization",
              header: "Organization",
              cell: (p) => p.organization,
            },
            {
              id: "status",
              header: "Status",
              cell: (p) =>
                p.status === "Active" ? (
                  <StatusIndicator type="success">Active</StatusIndicator>
                ) : (
                  <StatusIndicator type="pending">Pending</StatusIndicator>
                ),
            },
            { id: "grants", header: "Grants", cell: (p) => String(p.grant_count) },
          ]}
        />
      </Container>
    </ContentLayout>
  )
}
